import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Button, ButtonGroup, Chip, CircularProgress, Dialog, Tooltip, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetUsersQuery, useLazyGetUsersQuery, useUpdateUserMutation } from "../../api/apiSlice";
import { ConfirmDialog, CustomTabPanel, Table } from "../../components";
import { Drawer } from "../../components/Drawer/Drawer";
import UnauthorizedPage from "../../containers/ErrorStates/403";
import { useHasAdminRole, useHasPermission } from "../../hooks";
import { appendSearchParams, getApiErrorMessage } from "../../utils";
import { UserPermissions } from "../../utils/permissions";
import { IUser, ModalEnum, ModalType } from "../../utils/types";
import {
  ColumnView,
  ConfirmDialogEnum,
  ConfirmDialogType,
  defaultSortParams,
  getColumnVisibility,
  mapSortById,
  userTabParams,
} from "./constants";
import { CreateOrEditUser } from "./CreateOrEditUser";
import { exportUsersToCsv } from "./exportUsers";
import { UserStatus, UserTabEnum } from "./types";
import { useGetUsersTableColumns } from "./useGetUsersTableColumns";
import { ViewUser } from "./ViewUser";

export { userTabParams } from "./constants";

export const UsersPageContent = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const location = useLocation();
  const navigate = useNavigate();

  const [urlParams] = useState(new URLSearchParams(location.search));
  const [hoveredRow, setHoveredRow] = useState(-1);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [openedConfirmDialog, setOpenedConfirmDialog] = React.useState<ConfirmDialogType | undefined>();
  const [openUserDialog, setOpenUserDialog] = useState<ModalType | undefined>();
  const [activeDrawer, setActiveDrawer] = useState<ModalType | undefined>();
  const [filter, setFilter] = useState<string | undefined>();
  const [sorting, setSorting] = React.useState<{ id: string; desc: boolean }[]>([{ id: "createdAt", desc: true }]);
  const [columnFilters, setColumnFilters] = React.useState<{ id: string; value: any }[]>([]);
  const [activeView, setActiveView] = useState<ColumnView>("all");
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(getColumnVisibility("all"));
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedUsersMap, setSelectedUsersMap] = useState<Map<string, IUser>>(new Map());
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [showExportAllConfirm, setShowExportAllConfirm] = useState(false);

  const handleViewChange = (view: ColumnView) => {
    setActiveView(view);
    setColumnVisibility(getColumnVisibility(view));
  };

  const columnFiltersParams = columnFilters?.reduce<Record<string, string>>((acc, { id, value }) => {
    const key = mapSortById[id] || id;
    const formattedValue = ["updatedAt", "createdAt"].includes(id) ? dayjs(value).format("YYYY-MM-DD") : value;
    return { ...acc, [key]: formattedValue };
  }, {});

  const sortParams = sorting.reduce<{ sortBy: string; sortOrder: "DESC" | "ASC" }>((acc, { id, desc }) => {
    const sortBy = mapSortById[id] || id;
    return { sortBy, sortOrder: desc ? ("DESC" as const) : ("ASC" as const) };
  }, defaultSortParams);

  const [updateUser, updateUserResult] = useUpdateUserMutation();
  const [triggerGetAllUsers] = useLazyGetUsersQuery();

  const activeTabParam = (urlParams.get("activeTab") ?? userTabParams[0].value) as UserTabEnum;
  const activeTab = Math.max(
    0,
    userTabParams.findIndex(({ value }) => value === activeTabParam),
  );

  const apiSupportedParams = userTabParams[activeTab].params;

  const {
    data: userState,
    isLoading: loading,
    isFetching,
  } = useGetUsersQuery({
    ...apiSupportedParams,
    nameOrEmail: filter,
    per_page: pagination.pageSize,
    ...sortParams,
    page: pagination.pageIndex + 1,
    ...columnFiltersParams,
  });

  const usersData = useMemo(() => userState?.data ?? [], [userState?.data]);
  const total = userState?.total ?? 0;
  const prevUsersDataRef = useRef(usersData);

  // Sync accumulated selection map when checkboxes change on current page.
  // Adds newly checked users and removes unchecked ones from this page.
  useEffect(() => {
    // Skip when usersData just changed (page navigation) — the restore effect handles it
    if (prevUsersDataRef.current !== usersData) {
      prevUsersDataRef.current = usersData;
      return;
    }
    setSelectedUsersMap(prev => {
      const next = new Map(prev);
      const currentPageIds = new Set<string>(usersData.map((u: IUser) => String(u.id)));
      // Remove unchecked users from this page
      currentPageIds.forEach((id: string) => {
        if (!rowSelection[id]) next.delete(id);
      });
      // Add checked users from this page
      usersData.forEach((user: IUser) => {
        const id = String(user.id);
        if (rowSelection[id]) next.set(id, user);
      });
      return next;
    });
  }, [rowSelection, usersData]);

  // Restore checkboxes for the current page from the accumulated map
  useEffect(() => {
    const restored: Record<string, boolean> = {};
    usersData.forEach((user: IUser) => {
      const id = String(user.id);
      if (selectedUsersMap.has(id)) restored[id] = true;
    });
    setRowSelection(restored);
    // Only re-run when usersData changes (page navigation, not on rowSelection change)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersData]);

  // Clear all selections and reset to first page when filter/tab/search/sort changes (data set changes)
  useEffect(() => {
    setRowSelection({});
    setSelectedUsersMap(new Map());
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [activeTabParam, filter, sorting, columnFilters]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const activeTab = userTabParams[newValue].value;
    const newUrlParams = appendSearchParams({ activeTab }, urlParams);
    navigate(`${location.pathname}?${newUrlParams.toString()}`, {
      replace: true,
    });
  };

  const handleOpenEditUserDialog = () => {
    setOpenUserDialog(ModalEnum.Edit);
  };

  const handleOpenCreateUserDialog = () => {
    setSelectedUser(null);
    setHoveredRow(-1);
    setOpenUserDialog(ModalEnum.Create);
  };

  const handleCloseEditUserDialog = () => {
    setOpenUserDialog(undefined);
    setSelectedUser(null);
  };

  const handleToggleConfirmDialog = (dialog: ConfirmDialogType) => {
    setOpenedConfirmDialog(dialog);
  };

  const handleConfirmDialogClose = () => {
    setOpenedConfirmDialog(undefined);
  };

  useEffect(() => {
    if (updateUserResult.isSuccess) {
      handleCloseEditUserDialog();
    }
  }, [updateUserResult.isSuccess]);

  const handleStatusChange = (status: ConfirmDialogEnum) => {
    const messages = {
      [ConfirmDialogEnum.Activate]: {
        value: UserStatus.Active,
        success: "User activated successfully",
        error: "Failed to activate user",
      },
      [ConfirmDialogEnum.InActivate]: {
        value: UserStatus.Inactive,
        success: "User inactivated successfully",
        error: "Failed to inactivate user",
      },
    };

    if (selectedUser) {
      updateUser({
        id: selectedUser.id,
        status: messages[status].value,
      })
        .unwrap()
        .then(() => {
          handleConfirmDialogClose();
          toast.success(messages[status].success);
        })
        .catch(error => {
          const errorMessage = getApiErrorMessage(error, messages[status].error);
          toast.error(errorMessage);
        });
    } else {
      console.error("No user selected");
    }
  };

  const handlePreviewUser = () => {
    setActiveDrawer(ModalEnum.Preview);
  };

  const handleDrawerClose = () => {
    setActiveDrawer(undefined);
  };

  const columns = useGetUsersTableColumns({
    setHoveredRow,
    hoveredRow,
    onClickActivate: () => handleToggleConfirmDialog(ConfirmDialogEnum.Activate),
    onClickInactivate: () => handleToggleConfirmDialog(ConfirmDialogEnum.InActivate),
    onClickEditUser: handleOpenEditUserDialog,
    onClickPreview: handlePreviewUser,
    activeTab: activeTabParam,
  }).map(col => (["onboardingStatus", "customTrialStatus"].includes(col.accessorKey) ? { ...col, enableSorting: false } : { ...col, enableSorting: true }));

  const canCreateUsers = useHasPermission(UserPermissions.CREATE_USERS);

  const selectedUsers = useMemo(() => Array.from(selectedUsersMap.values()), [selectedUsersMap]);

  const hasOtherPageSelections = useMemo(() => {
    const currentPageIds = new Set(usersData.map((u: IUser) => String(u.id)));
    return Array.from(selectedUsersMap.keys()).some(id => !currentPageIds.has(id));
  }, [selectedUsersMap, usersData]);

  const selectionMessage = useMemo(() => {
    const count = selectedUsers.length;
    if (count === 0) return "";
    if (hasOtherPageSelections) return `${count} selected across pages`;
    return `${count} of ${total} row(s) selected`;
  }, [selectedUsers.length, hasOtherPageSelections, total]);

  const handleClearAllSelections = useCallback(() => {
    setSelectedUsersMap(new Map());
    setRowSelection({});
  }, []);

  const handleExportSelected = useCallback(() => {
    if (selectedUsers.length === 0) return;
    exportUsersToCsv(selectedUsers, columnVisibility, "users-selected");
  }, [selectedUsers, columnVisibility]);

  const handleExportAll = useCallback(async () => {
    setShowExportAllConfirm(false);
    setIsExportingAll(true);
    try {
      const result = await triggerGetAllUsers({
        ...apiSupportedParams,
        nameOrEmail: filter,
        per_page: 10000,
        ...sortParams,
        page: 1,
        ...columnFiltersParams,
      }).unwrap();
      const allUsers = result?.data ?? [];
      exportUsersToCsv(allUsers, columnVisibility, "users-all");
    } catch {
      toast.error("Failed to export users");
    } finally {
      setIsExportingAll(false);
    }
  }, [triggerGetAllUsers, apiSupportedParams, filter, sortParams, columnFiltersParams, columnVisibility]);

  const exportAllDialogBody = (
    <>
      <Typography sx={{ mb: 1.5 }}>
        This will export <strong>{total}</strong> user(s) to CSV with the following filters:
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={0.75}>
        <Chip label={`Tab: ${userTabParams[activeTab].label}`} size="small" variant="outlined" />
        {filter && <Chip label={`Search: "${filter}"`} size="small" variant="outlined" />}
        {columnFilters.map(({ id, value }) => (
          <Chip key={id} label={`${id}: ${value}`} size="small" variant="outlined" />
        ))}
        {!filter && columnFilters.length === 0 && <Chip label="No additional filters" size="small" variant="outlined" />}
      </Box>
    </>
  );

  return (
    <>
      <Dialog open={openUserDialog !== undefined} onClose={handleCloseEditUserDialog} fullWidth maxWidth="sm">
        {openUserDialog !== undefined && <CreateOrEditUser handleClose={handleCloseEditUserDialog} user={selectedUser} />}
      </Dialog>

      <Drawer title={`${activeDrawer} user`} onClose={handleDrawerClose} open={!!activeDrawer} sx={{ width: "500px", height: "100%" }}>
        {activeDrawer === ModalEnum.Preview && <ViewUser activeTab={activeTabParam} handleClose={handleDrawerClose} user={selectedUser} />}
      </Drawer>

      {openedConfirmDialog === ConfirmDialogEnum.Activate && (
        <ConfirmDialog
          open={true}
          title={`Activate ${selectedUser?.name}`}
          body="Are you sure you want to activate this user?"
          onClose={handleConfirmDialogClose}
          onConfirm={() => handleStatusChange(ConfirmDialogEnum.Activate)}
          loading={updateUserResult.isLoading}
        />
      )}

      {openedConfirmDialog === ConfirmDialogEnum.InActivate && (
        <ConfirmDialog
          open={true}
          title={`InActivate ${selectedUser?.name}`}
          body="Are you sure you want to inactivate this user?"
          onClose={handleConfirmDialogClose}
          onConfirm={() => handleStatusChange(ConfirmDialogEnum.InActivate)}
          loading={updateUserResult.isLoading}
        />
      )}

      <ConfirmDialog
        open={showExportAllConfirm}
        title="Export All Users"
        body={exportAllDialogBody}
        onClose={() => setShowExportAllConfirm(false)}
        onConfirm={handleExportAll}
        loading={isExportingAll}
      />

      <Box sx={{ width: "100%" }}>
        <Box display="flex" justifyContent="space-between">
          <Tabs value={activeTab} onChange={handleTabChange}>
            {userTabParams.map(tab => (
              <Tab
                key={tab.value}
                label={tab.label}
                sx={{
                  p: 2,
                  mr: 2,
                }}
              />
            ))}
          </Tabs>
          {canCreateUsers && (
            <Box>
              <Button onClick={handleOpenCreateUserDialog} sx={{ borderRadius: 0.5 }} color="primary" variant="outlined">
                CREATE USER
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ px: 2, pt: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Columns:
            </Typography>
            <ButtonGroup variant="outlined" size="small">
              {(["all", "subscription", "activity"] as ColumnView[]).map(view => (
                <Button key={view} onClick={() => handleViewChange(view)} variant={activeView === view ? "contained" : "outlined"}>
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip
              title={
                selectedUsers.length === 0
                  ? "Select rows across pages to export"
                  : `Export ${selectedUsers.length} selected user(s)`
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportSelected}
                  disabled={selectedUsers.length === 0}
                >
                  Export Selected ({selectedUsers.length})
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Exports all users in the current tab and search/filter results to CSV">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isExportingAll ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                  onClick={() => setShowExportAllConfirm(true)}
                  disabled={isExportingAll || total === 0}
                >
                  Export All ({total})
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ width: "100%" }}>
          <CustomTabPanel value={activeTab} index={activeTab}>
            <Table
              columns={columns}
              data={usersData}
              manualPagination
              manualSorting
              onPaginationChange={setPagination}
              enableFilters
              enableColumnFilterModes={false}
              manualFiltering
              onGlobalFilterChange={setFilter}
              enableRowSelection
              onRowSelectionChange={setRowSelection}
              getRowId={(row: IUser) => String(row.id)}
              initialState={{ showColumnFilters: false, sorting }}
              onColumnFiltersChange={setColumnFilters}
              onSortingChange={setSorting}
              renderToolbarAlertBannerContent={() => (
                <Box display="flex" alignItems="center" gap={1} sx={{ p: 1, visibility: selectedUsers.length > 0 ? "visible" : "hidden" }}>
                  <Typography variant="body2">
                    {selectionMessage}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    · Resets on filter, sort, search, or tab change
                  </Typography>
                  <Button size="small" onClick={handleClearAllSelections}>
                    Clear
                  </Button>
                </Box>
              )}
              muiToolbarAlertBannerProps={{
                sx: {
                  backgroundColor: "transparent",
                  color: "text.primary",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  "&.MuiAlert-standardInfo": {
                    backgroundColor: "transparent",
                  },
                  "& .MuiAlert-icon": {
                    color: "text.secondary",
                  },
                  "& .MuiAlert-message": {
                    width: "100%",
                    p: 0,
                  },
                },
              }}
              state={{ pagination, sorting, columnFilters, columnVisibility, rowSelection, showAlertBanner: true, columnPinning: { right: ["action"] } }}
              onColumnVisibilityChange={setColumnVisibility}
              rowCount={total}
              loading={loading || isFetching}
              muiTableBodyRowProps={({ row }: { row: { id: string; index: number; original: IUser } }) => ({
                onClick: () => {
                  handlePreviewUser();
                },
                onMouseEnter: () => {
                  setHoveredRow(row.index);
                  setSelectedUser(row.original);
                },
                onMouseLeave: () => {
                  setHoveredRow(-1);
                },
              })}
            />
          </CustomTabPanel>
        </Box>
      </Box>
    </>
  );
};

export const Users = () => {
  const { hasAdminRole: canManageUsers, isLoading } = useHasAdminRole();
  return !canManageUsers && !isLoading ? <UnauthorizedPage /> : <UsersPageContent />;
};
